import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { UserTypeEnum } from 'enums/userType';
import { JwtToken } from 'model/types';
import {
  getSessionStorageValueByKey,
  getSessionStorageValueByKeyNotArray,
  insertInSessionStorageByKey,
  insertInSessionStorageByKeyNotArray,
  setLogoutLocal,
  setLogoutSession,
  getVolCookie,
  setVolCookie,
  deleteVolCookie,
} from 'storage/session-storage';
// Application

const tokenKey = 'token';
const refreshTokenKey = 'refreshToken';
const expireInKey = 'expireIn';
const scopeKey = 'scope';
const tokenTypeKey = 'tokenType';

const VOL_AT_MAX_AGE = 3600;
const VOL_RT_MAX_AGE = 2592000; // 30 dias

const tokenChannel =
  typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('vol-sso-tokens')
    : null;

if (tokenChannel) {
  tokenChannel.onmessage = (e: MessageEvent) => {
    if (e.data?.type === 'logout') {
      ['vol_at', 'vol_rt', 'vol_tt', 'vol_exp', 'vol_sc'].forEach(deleteVolCookie);
    }
  };
}

// General

const producerUrlKey = 'producerUrl';
const customerIdKey = 'customerId';
const billingDateKey = 'billingDate';
const deliveryDateKey = 'deliveryDate';

// DirectedOffers

const modeListDirectedOffersKey = 'modeListDirectedOffers';

// MyOffers

const modeListMyOffersKey = 'modeListMyOffers';

// ShoppingCart

const typeOfferSelectedCartKey = 'typeOfferSelectedCart';

// MyOrders

const typeOfferSelectedMyOrdersKey = 'typeOfferSelectedMyOrders';

// ProductList

const modeListProductListKey = 'modeListProductList';
const letterSelectedProductListKey = 'letterSelectedProductList';
const filterStateKey = 'filterState';
const pageProductListKey = 'pageProductList';
const pageSizeProductListKey = 'pageSizeProductList';
const orderByProductListKey = 'orderByProductList';
const offerTypeProductListKey = 'offerTypeProductList';
const termProductListKey = 'termProductList';
const modePagination = 'modePagination';

// Local Storage

export const getAuthorizationToken = () => getVolCookie('vol_at');

export const setAuthorizationToken = (tokenValue: string) => {
  setVolCookie('vol_at', tokenValue, VOL_AT_MAX_AGE);
};

export const getScope = () => getVolCookie('vol_sc');

export const setScope = (scope: string) => {
  setVolCookie('vol_sc', scope, VOL_AT_MAX_AGE);
};

export const getRefreshTokenService = () => getVolCookie('vol_rt');

export const setRefreshToken = (refreshTokenValue: string) =>
  setVolCookie('vol_rt', refreshTokenValue, VOL_RT_MAX_AGE);

export const getTokenType = () => getVolCookie('vol_tt');

export const setTokenType = (tokenTypeValue: string) =>
  setVolCookie('vol_tt', tokenTypeValue, VOL_AT_MAX_AGE);

export const getExpireIn = () => getVolCookie('vol_exp');

export const setExpireIn = (expireInValue: string) => {
  const expSeconds = parseInt(expireInValue, 10) || 3600;
  setVolCookie('vol_exp', String(Math.floor(Date.now() / 1000) + expSeconds), expSeconds);
};

// Session Storage

export const getTermProductList = () =>
  getSessionStorageValueByKeyNotArray(termProductListKey);

export const setTermProductList = (termProductList: string) =>
  insertInSessionStorageByKeyNotArray(termProductListKey, termProductList);

export const getLetterSelectedProductList = () =>
  getSessionStorageValueByKeyNotArray(letterSelectedProductListKey);

export const setLetterSelectedProductList = (letterSelected: string) =>
  insertInSessionStorageByKeyNotArray(
    letterSelectedProductListKey,
    letterSelected,
  );

export const getCustomerIdSelected = () =>
  getSessionStorageValueByKeyNotArray(customerIdKey);

export const setProducerUrl = (producerUrl: string) =>
  insertInSessionStorageByKey(producerUrlKey, producerUrl);

export const getProducerUrl = (): string =>
  getSessionStorageValueByKey(producerUrlKey);

export const setCustomerIdSelected = (customerId: any) =>
  insertInSessionStorageByKeyNotArray(customerIdKey, String(customerId));

export const getOrderByProductList = () =>
  getSessionStorageValueByKeyNotArray(orderByProductListKey);

export const setOrderByProductList = (orderByProductList: string) =>
  insertInSessionStorageByKeyNotArray(
    orderByProductListKey,
    orderByProductList,
  );

export const getDeliveryDate = () =>
  getSessionStorageValueByKeyNotArray(deliveryDateKey);

export const setDeliveryDate = (deliveryDateValue: string) =>
  insertInSessionStorageByKeyNotArray(deliveryDateKey, deliveryDateValue);

export const getPageSizeProductList = () => {
  if (getSessionStorageValueByKeyNotArray(pageSizeProductListKey)) {
    return +getSessionStorageValueByKeyNotArray(pageSizeProductListKey);
  }
  return undefined;
};

export const setPageSizeProductList = (page: number) =>
  insertInSessionStorageByKeyNotArray(pageSizeProductListKey, String(page));

export const getPageProductList = () => {
  if (getSessionStorageValueByKeyNotArray(pageProductListKey)) {
    return +getSessionStorageValueByKeyNotArray(pageProductListKey);
  }
  return undefined;
};

export const setPageProductList = (page: number) =>
  insertInSessionStorageByKeyNotArray(pageProductListKey, String(page));

export const getModeListProductList = () => {
  if (getSessionStorageValueByKeyNotArray(modeListProductListKey)) {
    return +getSessionStorageValueByKeyNotArray(modeListProductListKey);
  }
  return 2;
};

export const setModeListProductList = (modeList: number) =>
  insertInSessionStorageByKeyNotArray(modeListProductListKey, String(modeList));

export const setFilterState = (filter: any) =>
  insertInSessionStorageByKeyNotArray(filterStateKey, JSON.stringify(filter));

export const getFilterState = () =>
  getSessionStorageValueByKeyNotArray(filterStateKey)
    ? JSON.parse(getSessionStorageValueByKeyNotArray(filterStateKey))
    : '';

export const getBillingDate = () =>
  getSessionStorageValueByKeyNotArray(billingDateKey);

export const setBillingDate = (billingDateValue: string) =>
  insertInSessionStorageByKeyNotArray(billingDateKey, billingDateValue);

export const getTypeOfferSelectedCart = () =>
  getSessionStorageValueByKeyNotArray(typeOfferSelectedCartKey);

export const setTypeOfferSelectedCart = (typeOfferSelectedCart: string) =>
  insertInSessionStorageByKeyNotArray(
    typeOfferSelectedCartKey,
    typeOfferSelectedCart,
  );

export const getTypeOfferSelectedMyOrders = () =>
  getSessionStorageValueByKeyNotArray(typeOfferSelectedMyOrdersKey);

export const setTypeOfferSelectedMyOrders = (
  typeOfferSelectedMyOrders: string,
) =>
  insertInSessionStorageByKeyNotArray(
    typeOfferSelectedMyOrdersKey,
    typeOfferSelectedMyOrders,
  );

export const getModeListMyOffers = () => {
  if (getSessionStorageValueByKeyNotArray(modeListMyOffersKey)) {
    return +getSessionStorageValueByKeyNotArray(modeListMyOffersKey);
  }
  return 1;
};

export const setModeListMyOffers = (modeListMyOffers: number) =>
  insertInSessionStorageByKeyNotArray(
    modeListMyOffersKey,
    String(modeListMyOffers),
  );

export const getModeListDirectedOffers = () => {
  if (getSessionStorageValueByKeyNotArray(modeListDirectedOffersKey)) {
    return +getSessionStorageValueByKeyNotArray(modeListDirectedOffersKey);
  }
  return 1;
};

export const setModeListDirectedOffers = (modeListDirectedOffers: number) =>
  insertInSessionStorageByKeyNotArray(
    modeListDirectedOffersKey,
    String(modeListDirectedOffers),
  );

export const getOfferTypeProductList = () =>
  parseValidating(
    getSessionStorageValueByKeyNotArray(offerTypeProductListKey),
    'array',
  ) as string[];

export const setOfferTypeProductList = (offerTypeProductList: string[]) =>
  insertInSessionStorageByKeyNotArray(
    offerTypeProductListKey,
    JSON.stringify(offerTypeProductList),
  );

export const setModePagination = (pagination: boolean) =>
  insertInSessionStorageByKeyNotArray(modePagination, `${pagination}`);

export const getModePagination = () =>
  getSessionStorageValueByKeyNotArray(modePagination);

// Remove Items

export const setLogout = () => {
  ['vol_at', 'vol_rt', 'vol_tt', 'vol_exp', 'vol_sc'].forEach(deleteVolCookie);
  setLogoutSession([
    customerIdKey,
    billingDateKey,
    deliveryDateKey,
    modeListDirectedOffersKey,
    modeListMyOffersKey,
    typeOfferSelectedCartKey,
    typeOfferSelectedMyOrdersKey,
    modeListProductListKey,
    filterStateKey,
    orderByProductListKey,
    letterSelectedProductListKey,
    pageProductListKey,
    pageSizeProductListKey,
    offerTypeProductListKey,
    termProductListKey,
    modePagination,
  ]);
  setLogoutLocal([
    tokenKey,
    scopeKey,
    expireInKey,
    tokenTypeKey,
    refreshTokenKey,
  ]);
};

export const removeItensShoppingCart = () => {
  setLogoutSession([typeOfferSelectedCartKey]);
};

export const removeItensDirectedOffers = () => {
  setLogoutSession([modeListDirectedOffersKey]);
};

export const removeItensMyOffers = () => {
  setLogoutSession([modeListMyOffersKey]);
};

export const removeItensMyOrders = () => {
  setLogoutSession([typeOfferSelectedMyOrdersKey]);
};

export const removeItensProductList = () => {
  setLogoutSession([
    modeListProductListKey,
    filterStateKey,
    orderByProductListKey,
    letterSelectedProductListKey,
    pageProductListKey,
    pageSizeProductListKey,
    offerTypeProductListKey,
    termProductListKey,
  ]);
};

// Functions

export const parseValidating = (value: string, typeParse: 'array' | any) => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch {
    if (typeParse === 'array') {
      return JSON.parse(`["${value}"]`);
    }
    return null;
  }
};

export const verifyIsProducer = (token: JwtToken): boolean => {
  if (token) {
    const roles = token?.veiling_claim_type_role;

    if (Array.isArray(roles)) {
      const containsRolesDiffProducer = roles.filter(
        (role) =>
          +role !== UserTypeEnum.PRODUCER &&
          +role !== UserTypeEnum.PRODUCER_EMPLOYEE,
      );
      return (
        !containsRolesDiffProducer || containsRolesDiffProducer.length <= 0
      );
    }

    return (
      +roles === UserTypeEnum.PRODUCER ||
      +roles === UserTypeEnum.PRODUCER_EMPLOYEE
    );
  }
  return false;
};

export const parseJwt = (token: string) => {
  try {
    const base64String = token.split('.')[1];
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    return JSON.parse(rawData);
  } catch (e) {
    return null;
  }
};

export const producerApi = axios.create({
  baseURL: process.env.REACT_APP_API_PRODUCER_URL,
});
producerApi.interceptors.request.use((config: AxiosRequestConfig) => {
  const token = getAuthorizationToken();
  if (config.headers) {
    config.headers.authorization = `Bearer ${token}`;
  }
  return config;
});

export const apiSession = axios.create({
  baseURL: process.env.REACT_APP_API_SESSION_URL,
});
apiSession.interceptors.request.use((config: AxiosRequestConfig) => {
  const token = getAuthorizationToken();
  if (config.headers) {
    config.headers.authorization = `Bearer ${token}`;
  }
  return config;
});

const baseApi = axios.create({
  baseURL: process.env.REACT_APP_API_ECOMMERCE_URL,
});
baseApi.interceptors.request.use((config: AxiosRequestConfig) => {
  const token = getAuthorizationToken();
  if (config.headers) {
    config.headers.authorization = `Bearer ${token}`;
  }
  return config;
});

let _refreshPromise: Promise<void> | null = null;

const doRefresh = async (): Promise<void> => {
  const refreshToken = getRefreshTokenService();
  const scope = getScope();
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refresh_token: refreshToken,
    client_id: process.env.REACT_APP_SSO_CLIENT_ID ?? '',
    client_secret: process.env.REACT_APP_SSO_CLIENT_SECRET ?? '',
    scope,
  });
  const { data } = await axios.post(
    `${process.env.REACT_APP_API_SESSION_URL}/connect/token`,
    body.toString(),
    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
  );
  setAuthorizationToken(data.access_token);
  setRefreshToken(data.refresh_token);
  setTokenType(data.token_type);
  setExpireIn(String(data.expires_in));
  if (data.scope) setScope(data.scope);
};

export const singleFlightRefresh = (): Promise<void> => {
  if (!_refreshPromise) {
    _refreshPromise = doRefresh().finally(() => {
      _refreshPromise = null;
    });
  }
  return _refreshPromise;
};

const attachRetryInterceptor = (instance: AxiosInstance): void => {
  instance.interceptors.response.use(
    res => res,
    async (error) => {
      const original = error.config;
      if (
        error?.response?.status === 401 &&
        original &&
        !original._retry &&
        !String(original.url ?? '').includes('/connect/token')
      ) {
        original._retry = true;
        // cross-tab guard: another tab may have already refreshed
        const sentAuth = original.headers?.authorization as string | undefined;
        const currentToken = getAuthorizationToken();
        if (sentAuth && sentAuth !== `Bearer ${currentToken}` && currentToken) {
          original.headers = original.headers ?? {};
          original.headers.authorization = `Bearer ${currentToken}`;
          return await instance(original);
        }
        try {
          await singleFlightRefresh();
          original.headers = original.headers ?? {};
          original.headers.authorization = `Bearer ${getAuthorizationToken()}`;
          return await instance(original);
        } catch {
          setLogout();
        }
      }
      return Promise.reject(error);
    },
  );
};

attachRetryInterceptor(producerApi);
attachRetryInterceptor(baseApi);

export default baseApi;
